import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { allenamentiTaxonomyEndpoints } from '../../../endpoints/allenamentiTaxonomy';
import {
  ICreateTrainingCategoryBody,
  IReorderTrainingCategoriesBody,
  ITrainingCategory,
  IUpdateTrainingCategoryBody,
} from '../../../models/allenamenti';
import { useDirectorId } from '../../../hooks/directorId';
import { readErrorMessage } from '../allenamenti.constants';

/** Bozza del form: una macro se `parentSlug` è vuoto, altrimenti una sotto. */
export interface ICategoryDraft {
  labelIt: string;
  color: string;
  parentSlug: string;
  isPhysical: boolean;
}

export const EMPTY_DRAFT: ICategoryDraft = {
  labelIt: '',
  color: '#4375C4',
  parentSlug: '',
  isPhysical: false,
};

/**
 * Creazione, modifica ed eliminazione delle categorie proprie della società.
 *
 * Le tre rotte restituiscono la tassonomia risolta completa: la si scrive
 * direttamente nella cache di `['allenamenti-taxonomy', directorId]` invece di
 * invalidare, così le tendine del form blocco si aggiornano senza un secondo
 * giro di rete — ed è la stessa chiave che usa `useAllenamentiTaxonomy`.
 */
export const useCategoriesManager = () => {
  const directorId = useDirectorId();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const writeCache = (categories: ITrainingCategory[]) => {
    queryClient.setQueryData(['allenamenti-taxonomy', directorId], categories);
  };

  const createMutation = useMutation({
    mutationFn: async (body: ICreateTrainingCategoryBody) =>
      (await allenamentiTaxonomyEndpoints.create(body)).data,
    onSuccess: (categories) => {
      writeCache(categories);
      setError(null);
    },
    onError: (e) => setError(readErrorMessage(e)),
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: { slug: string; body: IUpdateTrainingCategoryBody }) =>
      (await allenamentiTaxonomyEndpoints.update(vars.slug, vars.body)).data,
    onSuccess: (categories) => {
      writeCache(categories);
      setError(null);
    },
    onError: (e) => setError(readErrorMessage(e)),
  });

  const removeMutation = useMutation({
    mutationFn: async (slug: string) =>
      (await allenamentiTaxonomyEndpoints.remove(slug)).data,
    onSuccess: (categories) => {
      writeCache(categories);
      setError(null);
    },
    // Il 409 "categoria in uso" arriva qui: il messaggio del server dice già
    // quanti blocchi la usano e cosa fare, quindi si mostra quello.
    onError: (e) => setError(readErrorMessage(e)),
  });

  /**
   * Riordino, con la lista aggiornata SUBITO in cache.
   *
   * È l'unica mutation ottimistica delle quattro: le altre partono da un gesto
   * che accetta di aspettare (premo Salva), questa da una freccia che si preme
   * più volte di fila. Senza anticipo, ogni clic resterebbe fermo finché il
   * server non risponde e il secondo clic partirebbe dalla lista vecchia.
   *
   * In caso di errore si rimette la lista di prima: `onMutate` la restituisce
   * come contesto, e `onError` la riscrive.
   */
  const reorderMutation = useMutation({
    mutationFn: async (vars: {
      body: IReorderTrainingCategoriesBody;
      /** Come sarà la lista: serve solo all'anticipo, il server ricalcola. */
      optimistic: ITrainingCategory[];
    }) => (await allenamentiTaxonomyEndpoints.reorder(vars.body)).data,
    onMutate: (vars) => {
      const previous = queryClient.getQueryData<ITrainingCategory[]>([
        'allenamenti-taxonomy',
        directorId,
      ]);
      writeCache(vars.optimistic);
      return { previous };
    },
    onSuccess: (categories) => {
      writeCache(categories);
      setError(null);
    },
    onError: (e, _vars, context) => {
      if (context?.previous) writeCache(context.previous);
      setError(readErrorMessage(e));
    },
  });

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    removeMutation.isPending;

  return useMemo(
    () => ({
      error,
      clearError: () => setError(null),
      isSaving,
      create: (draft: ICategoryDraft) =>
        createMutation.mutateAsync({
          labelIt: draft.labelIt.trim(),
          color: draft.color,
          parentSlug: draft.parentSlug || null,
          isPhysical: draft.isPhysical,
        }),
      update: (slug: string, body: IUpdateTrainingCategoryBody) =>
        updateMutation.mutateAsync({ slug, body }),
      remove: (slug: string) => removeMutation.mutateAsync(slug),
      reorder: (
        body: IReorderTrainingCategoriesBody,
        optimistic: ITrainingCategory[],
      ) => reorderMutation.mutateAsync({ body, optimistic }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [error, isSaving],
  );
};
